import { useEffect, useState, useCallback, useRef } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageSquare, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";

// Inline conversation scoped to a single PROPERTY (all jobs at the property share it).
export default function PropertyThread({ propertyId, title }) {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const [thread, setThread] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [body, setBody] = useState("");
  const endRef = useRef(null);
  const qs = `scope=property&propertyId=${propertyId}`;

  const load = useCallback(() => {
    client.get(`/messages?${qs}`).then((r) => setThread(r.data.slice().reverse())).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    client.get(`/messages/receipts?${qs}`).then((r) => setReceipts(r.data || [])).catch(() => {});
    client.post("/messages/read", { convKey: "prop_" + propertyId }).catch(() => {});
  }, [propertyId, qs]);

  useEffect(() => { if (propertyId) load(); }, [propertyId, load]);
  usePoll(() => { if (propertyId) load(); }, 6000);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length]);

  const send = async () => {
    if (!body.trim()) return;
    try { await client.post("/messages", { scope: "property", propertyId, body }); setBody(""); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const lastMine = [...thread].reverse().find((m) => m.senderId === user?.id);
  const readers = lastMine ? receipts.filter((r) => r.lastReadAt && r.lastReadAt >= lastMine.createdAt) : [];

  return (
    <div className="flex flex-col h-[52vh] min-h-0 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="property-thread">
      <div className="h-11 flex items-center gap-2 px-4 border-b border-slate-100 shrink-0 bg-slate-50/60">
        <MessageSquare className="h-4 w-4 text-brand" />
        <span className="text-sm font-semibold text-slate-800 truncate">{title || t("prop.messages", "Property messages")}</span>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3 bg-slate-50/40" data-testid="property-thread-messages">
        {thread.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm"><MessageSquare className="h-8 w-8 mb-2" />{t("msg.noneProp", "No messages for this property yet.")}</div>
        ) : thread.map((m) => {
          const mine = m.senderId === user?.id;
          const showReceipt = mine && lastMine && m.id === lastMine.id;
          return (
            <div key={m.id} data-testid={`property-message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
                {!mine && <p className="text-[11px] font-semibold text-brand mb-0.5">{m.senderName}</p>}
                <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.createdAt).toLocaleString()}</p>
                {showReceipt && (
                  <p className="text-[10px] mt-0.5 flex items-center justify-end gap-1 text-white/90" data-testid="property-message-receipt">
                    {readers.length > 0
                      ? <><CheckCheck className="h-3 w-3" />{`${t("msg.readBy", "Read by")} ${readers.length}`}</>
                      : <><Check className="h-3 w-3" />{t("msg.delivered", "Delivered")}</>}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {can("message.send") && (
        <div className="border-t border-slate-200 p-3 flex gap-2 shrink-0">
          <Input data-testid="property-message-input" value={body} onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("msg.placeholder", "Write a message…")} />
          <Button data-testid="property-message-send" onClick={send} className="bg-brand hover:bg-brand-dark"><Send className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
